/**
 * CallDetailPanel — volledig overzicht van één call op /telefoon.
 *
 * Rendert: header (klant, richting, brand), recording player, events timeline,
 * quality stats en packet-loss sparkline. Consumeert /api/telephony/call-detail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CallRecordingPlayer } from './CallRecordingPlayer';
import { MaakOpvolgtaakButton } from './MaakOpvolgtaakButton';
import { LeerpuntToevoegenButton } from './LeerpuntToevoegenButton';
import { MeldVerbeterpunt } from '../agent/MeldVerbeterpunt';
import { PhoneNumber } from './PhoneNumber';
import type { Brand } from '../../lib/telephony';
import type { CallTranscriptSegment, CallProcessingStatus } from '../../lib/telephony/types';
import { BELREDENEN, effectiveCategory, labelFor, commsLeverFor } from '../../lib/telephony/belredenen';
import { useUserRole } from '../../hooks/useUserRole';

interface CallDetail {
  call: {
    id: string;
    direction: 'inbound' | 'outbound';
    from_number: string | null;
    to_number: string | null;
    brand: string;
    status: string;
    started_at: string;
    answered_at: string | null;
    ended_at: string | null;
    duration_seconds: number | null;
    recording_url: string | null;
    recording_duration_seconds: number | null;
    hangup_cause: string | null;
    notes: string | null;
    conversations: { customer_name: string | null; phone: string | null; customer_id: string | null } | null;
    transcript: CallTranscriptSegment[] | null;
    transcript_text: string | null;
    ai_summary: string | null;
    ai_topic: string | null;
    ai_sentiment: 'positief' | 'neutraal' | 'negatief' | null;
    ai_action_items: string[] | null;
    ai_followup_needed: boolean | null;
    processing_status: CallProcessingStatus | null;
    note_category: string | null;
    call_category: string | null;
    // Server-side afgeleid in api/_lib/call-afhandeling.ts; dezelfde bron als
    // de kolom "Afgehandeld door" in de historie-lijst.
    afhandeling: {
      soort: 'agent' | 'medewerker' | 'voicemail' | 'doorgeschakeld' | 'gemist' | null;
      naam: string | null;
    } | null;
  };
  events: Array<{
    event_type: string;
    source: string;
    metadata: Record<string, unknown>;
    created_at: string;
  }>;
  /** Audit-spoor van de voice-agent: wat hij deed (of in schaduw wilde doen). */
  agentActies?: Array<{
    created_at: string;
    actie: string;
    status: 'success' | 'schaduw' | 'geweigerd' | 'error' | string;
    params: Record<string, unknown> | null;
    resultaat: Record<string, unknown> | null;
  }>;
  quality: {
    p50: number | null;
    p95: number | null;
    packetLossPct: number | null;
    avgJitterMs: number | null;
    avgRttMs: number | null;
    samples: Array<{
      sampled_at: string;
      packets_lost: number | null;
      packets_received: number | null;
      jitter_ms: number | null;
      rtt_ms: number | null;
    }>;
  };
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function afhandelingTekst(a: CallDetail['call']['afhandeling']): string {
  switch (a?.soort) {
    case 'agent': return 'Voice-agent';
    case 'medewerker': return a.naam ?? 'Medewerker';
    case 'voicemail': return 'Voicemail';
    case 'doorgeschakeld': return 'Doorgeschakeld';
    case 'gemist': return 'Niemand';
    default: return '-';
  }
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function QualitySparkline({ samples }: { samples: CallDetail['quality']['samples'] }) {
  if (samples.length < 2) return null;

  const points = samples.map((s) => {
    const total = (s.packets_received ?? 0) + (s.packets_lost ?? 0);
    return total > 0 ? ((s.packets_lost ?? 0) / total) * 100 : 0;
  });
  const max = Math.max(5, ...points);
  const width = 240;
  const height = 36;
  const step = width / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(height - (p / max) * height).toFixed(1)}`)
    .join(' ');

  return (
    <svg width={width} height={height} aria-label="Packet loss over tijd" className="text-indigo-500">
      <path d={path} stroke="currentColor" strokeWidth={1.5} fill="none" />
    </svg>
  );
}

const SENTIMENT_STYLE: Record<string, string> = {
  positief: 'bg-green-100 text-green-800',
  neutraal: 'bg-slate-100 text-slate-700',
  negatief: 'bg-red-100 text-red-800',
};

const PROCESSING_LABEL: Record<string, string> = {
  pending: 'Transcript wordt verwerkt…',
  transcribing: 'Gesprek wordt getranscribeerd…',
  summarizing: 'Samenvatting wordt gemaakt…',
};

function SummaryBlock({
  call,
  onReprocess,
  reprocessing,
}: {
  call: CallDetail['call'];
  onReprocess: () => void;
  reprocessing: boolean;
}) {
  const status = call.processing_status;

  // In-progress states: show a lightweight indicator.
  if (status && PROCESSING_LABEL[status]) {
    return (
      <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
        <p className="text-sm text-indigo-700">{PROCESSING_LABEL[status]}</p>
      </div>
    );
  }

  // Failed: offer a retry.
  if (status === 'failed') {
    return (
      <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex items-center justify-between gap-3">
        <p className="text-sm text-red-700">Verwerking mislukt.</p>
        <button
          type="button"
          onClick={onReprocess}
          disabled={reprocessing}
          className="text-xs font-medium px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {reprocessing ? 'Bezig…' : 'Opnieuw proberen'}
        </button>
      </div>
    );
  }

  // No summary yet and not in a known processing state → nothing to show.
  if (!call.ai_summary && !call.ai_topic) return null;

  return (
    <div className="p-3 bg-slate-50 rounded-lg space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs font-medium text-slate-600">Samenvatting</p>
        {call.ai_topic && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
            {call.ai_topic}
          </span>
        )}
        {call.ai_sentiment && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${SENTIMENT_STYLE[call.ai_sentiment] ?? SENTIMENT_STYLE.neutraal}`}
          >
            {call.ai_sentiment}
          </span>
        )}
        {call.ai_followup_needed && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            Follow-up nodig
          </span>
        )}
      </div>

      {call.ai_summary && (
        <p className="text-sm text-slate-800 whitespace-pre-wrap">{call.ai_summary}</p>
      )}

      {call.ai_action_items && call.ai_action_items.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mt-1 mb-1">Actiepunten</p>
          <ul className="list-disc list-inside space-y-0.5 text-sm text-slate-700">
            {call.ai_action_items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CategoryBlock({
  call,
  onCorrect,
  saving,
}: {
  call: CallDetail['call'];
  onCorrect: (code: string) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const effective = effectiveCategory(call.note_category, call.call_category);
  const isManual = !!call.note_category;

  // Alleen tonen zodra er iets te tonen/corrigeren valt (verwerkt of al gezet).
  if (!effective && call.processing_status !== 'done') return null;

  const lever = commsLeverFor(effective);

  return (
    <div className="p-3 bg-slate-50 rounded-lg space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs font-medium text-slate-600">Belreden</p>
        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
          {effective ? labelFor(effective) : 'Nog niet bepaald'}
        </span>
        {effective && (
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            {isManual ? 'handmatig' : 'AI'}
          </span>
        )}
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="ml-auto text-xs text-indigo-600 hover:text-indigo-800 underline"
          >
            Corrigeren
          </button>
        )}
      </div>

      {lever && (
        <p className="text-xs text-slate-500">{lever}</p>
      )}

      {editing && (
        <div className="flex items-center gap-2">
          <select
            defaultValue={call.note_category ?? ''}
            disabled={saving}
            onChange={(e) => {
              onCorrect(e.target.value);
              setEditing(false);
            }}
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="">(automatisch bepalen — AI leidend)</option>
            {BELREDENEN.map((b) => (
              <option key={b.code} value={b.code}>{b.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Annuleren
          </button>
        </div>
      )}
    </div>
  );
}

function TranscriptBlock({ segments }: { segments: CallTranscriptSegment[] }) {
  const [open, setOpen] = useState(false);
  if (segments.length === 0) return null;

  return (
    <div className="bg-slate-50 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
      >
        <span>Transcript ({segments.length} fragmenten)</span>
        <span className="text-slate-400">{open ? 'Verbergen' : 'Tonen'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 max-h-[360px] overflow-y-auto">
          {segments.map((seg, i) => {
            const isAgent = seg.role === 'medewerker';
            return (
              <div key={i} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${
                    isAgent
                      ? 'bg-indigo-100 text-indigo-900'
                      : 'bg-white border border-slate-200 text-slate-800'
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
                    {seg.role === 'medewerker' ? 'Medewerker' : seg.role === 'klant' ? 'Klant' : 'Onbekend'}
                  </p>
                  {seg.text}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface CallDetailPanelProps {
  callId: string | null;
  onClose: () => void;
  /** 'inline' renders a bordered card below a list; 'drawer' fills a side panel. */
  variant?: 'inline' | 'drawer';
}

export function CallDetailPanel({ callId, onClose, variant = 'inline' }: CallDetailPanelProps) {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const isDrawer = variant === 'drawer';
  const [detail, setDetail] = useState<CallDetail | null>(null);
  const [idGekopieerd, setIdGekopieerd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [superviseState, setSuperviseState] = useState<'idle' | 'busy' | 'joined'>('idle');
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Live meeluisteren (experimenteel, admin): trekt het actieve gesprek in
  // een Telnyx-conference en dialt je eigen softphone erin als supervisor.
  // Je browser rinkelt; na opnemen luister je mee. monitor = niemand hoort
  // je; whisper = alleen de medewerker hoort je (coachen/tips geven).
  // Inbound (v5): de klant gaat eerst de conference in en de softphone van de
  // medewerker rinkelt kort opnieuw — zij moet die terugbel-leg aannemen om
  // weer bij de klant te komen. Alleen op testgesprekken tot de belproef
  // groen is (docs/telefonie-inwerken.md).
  const startSupervise = useCallback(async (mode: 'monitor' | 'whisper') => {
    if (!detail || superviseState === 'busy') return;
    setSuperviseState('busy');
    setActionError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Niet ingelogd');
      const res = await fetch('/api/telephony/supervise', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ callId: detail.call.id, mode }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSuperviseState('joined');
    } catch (err) {
      setSuperviseState('idle');
      setActionError(err instanceof Error ? err.message : 'Meeluisteren starten mislukt');
    }
  }, [detail, superviseState]);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    setDetail(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch(`/api/telephony/call-detail?id=${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDetail(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, []);

  const reprocess = useCallback(async () => {
    if (!detail) return;
    setReprocessing(true);
    setActionError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch('/api/telephony/reprocess', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ callId: detail.call.id }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setActionError(body?.error || `Opnieuw verwerken mislukt (${res.status})`);
        return;
      }
      await fetchDetail(detail.call.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Opnieuw verwerken mislukt');
    } finally {
      setReprocessing(false);
    }
  }, [detail, fetchDetail]);

  // Correctie van de belreden: schrijf note_category (mens-override). Leeg
  // ('') wist de override zodat de AI-categorie weer leidend wordt.
  const correctCategory = useCallback(async (code: string) => {
    if (!detail) return;
    setSavingCategory(true);
    setActionError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch('/api/telephony/callback', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ callId: detail.call.id, note_category: code }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setActionError(body?.error || `Categorie opslaan mislukt (${res.status})`);
        return;
      }
      await fetchDetail(detail.call.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Categorie opslaan mislukt');
    } finally {
      setSavingCategory(false);
    }
  }, [detail, fetchDetail]);

  useEffect(() => {
    if (!callId) { setDetail(null); return; }
    void fetchDetail(callId);
  }, [callId, fetchDetail]);

  // Live gesprekken zelf-verversen: de status flipt serverside van ringing →
  // active → completed terwijl het paneel openstaat (anders bleef "Rinkelt"
  // staan en verscheen de meeluister-knop nooit; gezien belproef 2026-07-07).
  const liveStatus = detail?.call.status;
  useEffect(() => {
    if (!callId) return;
    if (liveStatus !== 'ringing' && liveStatus !== 'active') return;
    const t = setInterval(() => {
      void (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) return;
          const res = await fetch(`/api/telephony/call-detail?id=${encodeURIComponent(callId)}`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (res.ok) setDetail(await res.json());
        } catch {
          // stil: volgende tick probeert opnieuw
        }
      })();
    }, 5000);
    return () => clearInterval(t);
  }, [callId, liveStatus]);

  // Inline mode renders below a long history table — scroll it into view when a
  // call is selected so the click visibly "does something". In drawer mode the
  // panel is already in view, so skip it.
  useEffect(() => {
    if (!isDrawer && callId && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [callId, isDrawer]);

  if (!callId) return null;

  return (
    <div
      ref={panelRef}
      className={
        isDrawer
          ? 'h-full flex flex-col bg-white overflow-hidden'
          : 'bg-white border border-slate-200 rounded-xl overflow-hidden mt-4 scroll-mt-4'
      }
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-700">Gespreksdetails</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Details sluiten"
          className="text-xs text-slate-500 hover:text-slate-800"
        >
          Sluiten
        </button>
      </div>

      <div className={isDrawer ? 'flex-1 overflow-y-auto' : ''}>
      {loading && (
        <div className="p-6 text-center text-sm text-slate-400">Laden...</div>
      )}
      {error && (
        <div className="p-6 text-center text-sm text-red-500">{error}</div>
      )}

      {detail && (
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-slate-400 text-xs">Klant</p>
              {detail.call.conversations?.customer_name && detail.call.conversations?.customer_id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/klanten/${detail.call.conversations!.customer_id}`)}
                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium truncate text-left"
                  title="Open klant"
                >
                  {detail.call.conversations.customer_name}
                </button>
              ) : (
                <p className="text-slate-800 font-medium truncate">
                  {detail.call.conversations?.customer_name || 'Onbekend'}
                </p>
              )}
            </div>
            <div>
              <p className="text-slate-400 text-xs">Nummer</p>
              <p className="text-slate-700 font-mono text-xs">
                <PhoneNumber
                  number={detail.call.direction === 'inbound' ? detail.call.from_number : detail.call.to_number}
                  brand={detail.call.brand as Brand}
                  sourceContext="call-detail"
                  className="font-mono"
                />
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Merk</p>
              <p className="text-slate-700">{detail.call.brand}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Duur</p>
              <p className="text-slate-700">{formatDuration(detail.call.duration_seconds)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Afgehandeld door</p>
              <p className={detail.call.afhandeling?.soort === 'agent'
                ? 'text-violet-700 font-medium'
                : 'text-slate-700'}>
                {afhandelingTekst(detail.call.afhandeling)}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Start</p>
              <p className="text-slate-700 text-xs">{formatDateTime(detail.call.started_at)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Opgenomen</p>
              <p className="text-slate-700 text-xs">{formatDateTime(detail.call.answered_at)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Einde</p>
              <p className="text-slate-700 text-xs">{formatDateTime(detail.call.ended_at)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Hangup</p>
              <p className="text-slate-700 text-xs">{detail.call.hangup_cause || '-'}</p>
            </div>
            <div className="col-span-2">
              {/* Gesprek-id (07-09): het handvat voor /gesprek-analyse <id> in
                  een Claude-sessie; het dump-script vindt hiermee de
                  bridge-sessie, de audit en dit calls-record. */}
              <p className="text-slate-400 text-xs">Gesprek-id</p>
              <p className="text-slate-700 text-xs font-mono flex items-center gap-2">
                <span className="truncate" title={detail.call.id}>{detail.call.id}</span>
                <button
                  type="button"
                  className="text-blue-600 hover:underline font-sans shrink-0"
                  onClick={() => {
                    void navigator.clipboard?.writeText(`/gesprek-analyse ${detail.call.id}`).then(
                      () => { setIdGekopieerd(true); setTimeout(() => setIdGekopieerd(false), 1500); },
                      () => {},
                    );
                  }}
                >
                  {idGekopieerd ? 'gekopieerd' : 'kopieer als /gesprek-analyse'}
                </button>
              </p>
            </div>
          </div>

          {/* Vangnet: handmatig een opvolgtaak op de Opvolgen-lijst zetten. */}
          <div className="flex justify-end items-center gap-2">
            {role === 'admin' && detail.call.status === 'active' && (
              <>
                <button
                  type="button"
                  onClick={() => void startSupervise('monitor')}
                  disabled={superviseState !== 'idle'}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                  title="Experimenteel, alleen op testgesprekken. Jouw softphone rinkelt en na opnemen luister je onhoorbaar mee; bij inkomende gesprekken rinkelt ook de softphone van de medewerker kort opnieuw."
                >
                  {superviseState === 'busy'
                    ? 'Verbinden…'
                    : superviseState === 'joined'
                      ? 'Meeluisteren gestart, neem op'
                      : 'Meeluisteren (stil)'}
                </button>
                {superviseState === 'idle' && (
                  <button
                    type="button"
                    onClick={() => void startSupervise('whisper')}
                    disabled={superviseState !== 'idle'}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                    title="Experimenteel, alleen op testgesprekken. Alleen de medewerker hoort jou (tips geven); de klant hoort jou niet. Bij inkomende gesprekken rinkelt de softphone van de medewerker kort opnieuw."
                  >
                    Coachen (whisper)
                  </button>
                )}
              </>
            )}
            {/*
              Alleen bij voice-agent-gesprekken: een leerpunt gaat over het
              gedrag van de agent, en het endpoint is admin-only. De coach ziet
              niet alles (trigger-casus WB-322232), dus dit is het pad voor wat
              jij zelf hoort bij het naluisteren.
            */}
            {role === 'admin' && detail.call.afhandeling?.soort === 'agent' && (
              <LeerpuntToevoegenButton
                callId={detail.call.id}
                brand={detail.call.brand}
                defaultBewijs={
                  detail.agentActies?.length
                    ? `Agent-acties: ${detail.agentActies.map((a) => a.actie).filter(Boolean).join(', ')}`
                    : undefined
                }
              />
            )}
            <MeldVerbeterpunt key={detail.call.id} kanaal="voice" bronId={detail.call.id} />
            <MaakOpvolgtaakButton
              sourceRef={detail.call.id}
              customerId={detail.call.conversations?.customer_id ?? undefined}
              customerName={detail.call.conversations?.customer_name ?? undefined}
              brand={detail.call.brand}
              defaultTitle={
                detail.call.conversations?.customer_name
                  ? `Terugbellen: ${detail.call.conversations.customer_name}`
                  : ''
              }
            />
          </div>

          {/* Recording */}
          {detail.call.recording_url && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-slate-600 mb-2">Opname</p>
              <CallRecordingPlayer
                callId={detail.call.id}
                durationSeconds={detail.call.recording_duration_seconds ?? detail.call.duration_seconds ?? undefined}
              />
            </div>
          )}

          {actionError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
              {actionError}
            </div>
          )}

          {/* AI summary */}
          <SummaryBlock call={detail.call} onReprocess={reprocess} reprocessing={reprocessing} />

          {/* Belreden (effectief: mens-override boven AI) + inline correctie */}
          <CategoryBlock call={detail.call} onCorrect={(code) => void correctCategory(code)} saving={savingCategory} />

          {/* Transcript */}
          {detail.call.transcript && detail.call.transcript.length > 0 && (
            <TranscriptBlock segments={detail.call.transcript} />
          )}

          {/* Disposition notes */}
          {detail.call.notes && (
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs font-medium text-amber-800 mb-1">Notitie</p>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">{detail.call.notes}</p>
            </div>
          )}

          {/* Agent-acties: wat de voice-agent in het systeem deed (of in
              schaduw wilde doen) — het audit-spoor per gesprek. */}
          {detail.agentActies && detail.agentActies.length > 0 && (
            <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg">
              <p className="text-xs font-medium text-violet-800 mb-2">
                Agent-acties ({detail.agentActies.length})
              </p>
              <ul className="space-y-2">
                {detail.agentActies.map((a, i) => (
                  <li key={i} className="text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-400 font-mono shrink-0">
                        {new Date(a.created_at).toLocaleTimeString('nl-NL', {
                          hour: '2-digit', minute: '2-digit', second: '2-digit',
                        })}
                      </span>
                      <span className="font-medium text-slate-800">{a.actie}</span>
                      <span
                        className={
                          'px-1.5 py-0.5 rounded text-[10px] font-medium ' +
                          (a.status === 'schaduw'
                            ? 'bg-amber-100 text-amber-800'
                            : a.status === 'success'
                              ? 'bg-emerald-100 text-emerald-800'
                              : a.status === 'geweigerd' || a.status === 'afgewezen'
                                ? 'bg-slate-200 text-slate-600'
                                : 'bg-red-100 text-red-700')
                        }
                      >
                        {a.status === 'schaduw'
                          ? 'schaduw — niet uitgevoerd'
                          : a.status === 'success'
                            ? 'uitgevoerd'
                            : a.status === 'geweigerd'
                              ? 'geweigerd'
                              : a.status === 'afgewezen'
                                ? 'niet gelukt (poort of primitief)'
                                : 'fout'}
                      </span>
                    </div>
                    {typeof a.resultaat?.uitleg === 'string' && (
                      <p className="mt-0.5 text-slate-600 pl-16">{a.resultaat.uitleg}</p>
                    )}
                    <details className="pl-16 mt-0.5">
                      <summary className="text-[10px] text-violet-400 cursor-pointer select-none">
                        details
                      </summary>
                      <pre className="mt-1 p-2 bg-white border border-violet-100 rounded text-[10px] text-slate-600 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify({ params: a.params, resultaat: a.resultaat }, null, 2)}
                      </pre>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quality */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-slate-600 mb-2">Verbindingskwaliteit</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-slate-400">Pakketverlies</p>
                  <p className="text-slate-800 font-mono">
                    {detail.quality.packetLossPct !== null
                      ? `${Number(detail.quality.packetLossPct).toFixed(1)}%`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Gem. jitter</p>
                  <p className="text-slate-800 font-mono">
                    {detail.quality.avgJitterMs !== null
                      ? `${Number(detail.quality.avgJitterMs).toFixed(1)} ms`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Gem. RTT</p>
                  <p className="text-slate-800 font-mono">
                    {detail.quality.avgRttMs !== null
                      ? `${Number(detail.quality.avgRttMs).toFixed(0)} ms`
                      : '-'}
                  </p>
                </div>
              </div>
              <div className="mt-2">
                <QualitySparkline samples={detail.quality.samples} />
              </div>
            </div>

            {/* Events timeline */}
            <div className="p-3 bg-slate-50 rounded-lg max-h-[240px] overflow-y-auto">
              <p className="text-xs font-medium text-slate-600 mb-2">
                Events ({detail.events.length})
              </p>
              {detail.events.length === 0 ? (
                <p className="text-xs text-slate-400">Geen events</p>
              ) : (
                <ul className="space-y-0.5 text-xs font-mono">
                  {detail.events.map((e, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400 shrink-0">
                        {new Date(e.created_at).toLocaleTimeString('nl-NL', {
                          hour: '2-digit', minute: '2-digit', second: '2-digit',
                        })}
                      </span>
                      <span className="text-slate-800">{e.event_type}</span>
                      <span className="text-slate-400 text-[10px]">{e.source}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
